import type {Id} from "@typesDef/general.types";

export type HydrationLevel = "none" | "partial" | "full";

export interface EntityMeta {
    hydration: HydrationLevel;
    loadedAt?: number;
}

export interface ListMeta {
    ids: Id[];
    loaded: boolean;
    fullyLoaded: boolean;
    limit: number;
    skip: number;
    total?: number;
}


/**
 * Sets the target array to contain unique IDs from the provided ids array, while maintaining the order of the first occurrence of each ID.
 * @param target The array to be updated with unique IDs.
 * @param ids The array of IDs from which to extract unique values.
 */
export function setIdsUnique(target: Id[], ids: Id[]) {
    // keep order from ids, drop duplicates
    const seen = new Set<Id>()
    target.length = 0
    for (const id of ids) {
        if (!seen.has(id)) {
            seen.add(id)
            target.push(id)
        }
    }
}

export function createListMeta(partial: Partial<ListMeta> = {}): ListMeta {
    return {
        ids: partial.ids ?? [],
        loaded: partial.loaded ?? false,
        fullyLoaded: partial.fullyLoaded ?? false,
        limit: partial.limit ?? 0,
        skip: partial.skip ?? 0,
        total: partial.total,
    };
}

export function mergeHydrationLevel(
    current: HydrationLevel | undefined,
    next: HydrationLevel
): HydrationLevel {
    const rank: Record<HydrationLevel, number> = {
        none: 0,
        partial: 1,
        full: 2,
    };

    if (!current) return next;
    return rank[next] > rank[current] ? next : current;
}

export function markEntityHydration(
    metaById: Record<Id, EntityMeta>,
    id: Id,
    nextHydration: HydrationLevel
) {
    const prev = metaById[id];
    metaById[id] = {
        ...prev,
        hydration: mergeHydrationLevel(prev?.hydration, nextHydration),
        loadedAt: Date.now(),
    };
}

export function upsertEntityWithMeta<T extends {_id: Id}>(
    byId: Record<Id, T>,
    metaById: Record<Id, EntityMeta>,
    entity: T,
    hydration: HydrationLevel,
    mergeFn: (oldEntity: T | undefined, newEntity: T) => T
): T {
    const merged = mergeFn(byId[entity._id], entity);
    byId[entity._id] = merged;
    markEntityHydration(metaById, entity._id, hydration);
    return merged;
}

export function updateListMeta(
    meta: ListMeta,
    payload: {
        ids: Id[];
        limit: number;
        skip: number;
        total?: number;
        append?: boolean;
    }
): ListMeta {
    const append = payload.append === true;
    const mergedIds = append ? [...meta.ids, ...payload.ids] : payload.ids;
    const uniqueIds: Id[] = [];
    setIdsUnique(uniqueIds, mergedIds);

    const knownTotal = payload.total ?? meta.total;
    const fullyLoaded = knownTotal !== undefined
        ? uniqueIds.length >= knownTotal
        : payload.ids.length < payload.limit;

    return {
        ids: uniqueIds,
        loaded: true,
        fullyLoaded,
        limit: payload.limit,
        skip: payload.skip,
        total: knownTotal,
    };
}

export async function updateAndRefresh<T>(
    updateFn: () => Promise<void>,
    fetchFn: () => Promise<T>,
    storeUpdater: (updated: T) => void
): Promise<T> {
    await updateFn();
    const updated = await fetchFn();
    storeUpdater(updated);
    return updated;
}
export async function updateAndRefreshFrom<T>(
    updateFn: () => Promise<Partial<T>>,
    fetchFn: () => Promise<T>,
    storeUpdater: (updated: T) => void
): Promise<T> {
    await updateFn(); // Optionally do something with response here
    const updated = await fetchFn();
    storeUpdater(updated);
    return updated;
}
